import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { createReleaseController } from './releaseController';
import { browserReleaseDependencies, monitorBrowserRelease } from './browserReleaseAdapter';
import './release.css';

// Outside Auth0Provider: no callback processing, workflow effects or calls before the startup check.
export default function ReleaseBoundary({
  children,
  dependencies,
  enabled = process.env.NODE_ENV === 'production' && process.env.REACT_APP_RELEASE_UPDATES_ENABLED !== 'false',
}) {
  const controllerRef = useRef(null);
  const [state, setState] = useState({ ready: !enabled });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const updateButton = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = createReleaseController(dependencies || browserReleaseDependencies());
    controllerRef.current = controller;
    const unsubscribe = controller.subscribe(setState);
    const unmonitor = monitorBrowserRelease(controller);
    controller.start();
    return () => {
      unsubscribe();
      unmonitor();
      controller.stop();
    };
  }, [enabled, dependencies]);
  const diagnostics = `Loaded build: ${state.loadedBuild ||
    'unavailable'}\nLatest observed build: ${state.latestBuild ||
    state.loadedBuild ||
    'unavailable'}\nLast successful check: ${state.lastCheckedAt || 'unavailable'}`;
  const refresh = async () => {
    setBusy(true);
    await controllerRef.current.refresh();
    setBusy(false);
    setConfirm(false);
  };
  if (!state.ready)
    return (
      <div role="status" className="release-startup">
        Checking for updates…
      </div>
    );
  return (
    <>
      {state.latestBuild && (
        <aside className="release-notice" aria-label="Application update">
          <span role="status">
            {state.recovery
              ? 'The latest version has not loaded yet. You can continue working and try updating again.'
              : 'A new version of RezRATE is available.'}
          </span>
          <button ref={updateButton} type="button" onClick={() => setConfirm(true)}>
            Update
          </button>
          <details>
            <summary>Update details</summary>
            <pre>{diagnostics}</pre>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(diagnostics);
                  setCopied(true);
                } catch (_) {
                  setCopied(false);
                }
              }}
            >
              Copy diagnostics
            </button>
            {copied && <span role="status">Copied</span>}
          </details>
          {state.error && <p role="status">{state.error}</p>}
        </aside>
      )}
      {children}
      <Modal
        isOpen={confirm}
        toggle={() => !busy && setConfirm(false)}
        labelledBy="release-confirm-title"
        onClosed={() => updateButton.current && updateButton.current.focus()}
      >
        <ModalHeader id="release-confirm-title" toggle={() => !busy && setConfirm(false)}>
          Update RezRATE?
        </ModalHeader>
        <ModalBody>
          Refreshing may discard unsaved changes or interrupt a call. Save your work and finish any call before
          continuing.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={busy} onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button color="primary" disabled={busy} onClick={refresh}>
            {busy ? 'Checking…' : 'Refresh now'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
